import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AdaptiveLayout from "@/components/AdaptiveLayout";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import MemberLayout from "@/components/MemberLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import InstallPrompt from "@/components/InstallPrompt";
import { Toaster } from "sonner";
import CookieConsent from "@/components/CookieConsent";

/*
 * Code-splitting policy — 2026-08-03:
 * -----------------------------------
 * `Landing` stays eager because it is the SEO-critical first paint on
 * `/`. Every other route is split into its own async chunk so the initial
 * JS payload only carries the code needed for the landing page + the auth
 * providers. Marketing pages, the member app and the admin panel are now
 * fetched on-demand, which addresses the SEO audit's "slow pages" flag.
 */
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Profile = lazy(() => import("@/pages/Profile"));
const Members = lazy(() => import("@/pages/Members"));
const MemberDetail = lazy(() => import("@/pages/MemberDetail"));
const Operators = lazy(() => import("@/pages/Operators"));
const TravelPlanner = lazy(() => import("@/pages/TravelPlanner"));
const MyBlog = lazy(() => import("@/pages/MyBlog"));
const SupportContact = lazy(() => import("@/pages/SupportContact"));
const Home = lazy(() => import("@/pages/Home"));
const GlobalServices = lazy(() => import("@/pages/GlobalServices"));
const GlobalServiceNew = lazy(() => import("@/pages/GlobalServiceNew"));
const GlobalServiceDetail = lazy(() => import("@/pages/GlobalServiceDetail"));
const Forum = lazy(() => import("@/pages/Forum"));
const ForumNew = lazy(() => import("@/pages/ForumNew"));
const ForumTopic = lazy(() => import("@/pages/ForumTopic"));
const Ads = lazy(() => import("@/pages/Ads"));
const AdNew = lazy(() => import("@/pages/AdNew"));
const AdDetail = lazy(() => import("@/pages/AdDetail"));
const AdReturn = lazy(() => import("@/pages/AdReturn"));
const Messages = lazy(() => import("@/pages/Messages"));
const Newsletter = lazy(() => import("@/pages/Newsletter"));
const Billing = lazy(() => import("@/pages/Billing"));
const BillingReturn = lazy(() => import("@/pages/BillingReturn"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const StartCheckout = lazy(() => import("@/pages/StartCheckout"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Cookies = lazy(() => import("@/pages/Cookies"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ReferralCapture = lazy(() => import("@/pages/ReferralCapture"));
const Guides = lazy(() => import("@/pages/Guides"));
const GuideDetail = lazy(() => import("@/pages/GuideDetail"));

/**
 * Register the service worker on window load. Doing it after `load` means
 * the initial page paint isn't delayed by SW installation. Silent-fails in
 * environments that don't support it (older browsers, http:// preview).
 */
function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register on secure contexts. localhost is treated as secure by browsers.
  if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(err => {
      // Non-fatal: PWA installability degrades, everything else still works.
      console.warn("Service worker registration failed:", err);
    });
  });
}

/**
 * Suspense fallback while a code-split chunk loads. Kept intentionally
 * minimal (single pulsing dot in accent color) so it doesn't flash a full
 * skeleton for chunks that arrive in <200ms on a warm cache.
 */
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-suspense-fallback">
      <div className="w-2.5 h-2.5 rounded-full bg-[var(--hh-accent)] animate-pulse" />
    </div>
  );
}

function App() {
  // Analytics no longer auto-installs — it boots from <CookieConsent /> only
  // after the user grants consent (GDPR / ePrivacy). See analytics.js.
  useEffect(() => { registerServiceWorker(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public pages */}
              <Route element={<Layout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/r/:code" element={<ReferralCapture />} />
                <Route path="/checkout" element={<StartCheckout />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/forum/tips" element={<Navigate to="/guides/esim" replace />} />
              </Route>

              {/* Guides — public for SEO, inside the member shell when signed in */}
              <Route element={<AdaptiveLayout />}>
                <Route path="/guides" element={<Guides />} />
                <Route path="/guides/:slug" element={<GuideDetail />} />
              </Route>

              {/* Member app — left sidebar layout */}
              <Route element={<ProtectedRoute><MemberLayout /></ProtectedRoute>}>
                <Route path="/home" element={<Home />} />
                <Route path="/dashboard" element={<Home />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/members" element={<Members />} />
                <Route path="/members/:id" element={<MemberDetail />} />
                <Route path="/operators" element={<Operators />} />
                <Route path="/blog" element={<MyBlog />} />
                <Route path="/travel" element={<TravelPlanner />} />
                <Route path="/forum" element={<Forum />} />
                <Route path="/forum/new" element={<ForumNew />} />
                <Route path="/forum/:id" element={<ForumTopic />} />
                <Route path="/community-ads" element={<Ads />} />
                <Route path="/community-ads/new" element={<AdNew />} />
                <Route path="/community-ads/return" element={<AdReturn />} />
                <Route path="/community-ads/:id" element={<AdDetail />} />
                <Route path="/global-services" element={<GlobalServices />} />
                <Route path="/global-services/new" element={<GlobalServiceNew />} />
                <Route path="/global-services/:id" element={<GlobalServiceDetail />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/messages/:otherId" element={<Messages />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/billing/return" element={<BillingReturn />} />
                <Route path="/support" element={<SupportContact />} />
              </Route>

              {/* Admin (no public layout) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminPanel />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
        <Toaster richColors theme="dark" />
        <InstallPrompt />
      </AuthProvider>
    </div>
  );
}

export default App;
